import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '~/single-page/constants/routes';

const REJECTION_REASONS = {
  incomplete_information: 'Informações Incompletas',
  duplicate: 'Cadastro Duplicado',
  not_eligible: 'Não Elegível',
  suspicious: 'Atividade Suspeita',
  other: 'Outros Motivos'
};

export default function RegistrationStatusAlert({ status, rejectionReason, observations, registrationData }) {
  const theme = useTheme();
  const navigate = useNavigate();

  if (!status) return null;

  const handleEditRegistration = () => {
    const formattedData = {
      ngo: registrationData.ngo || {},
      user: registrationData.user || {},
      status: registrationData.status || status,
      rejectionReason: registrationData.rejectionReason || rejectionReason,
      observations: registrationData.observations || observations
    };

    navigate(APP_ROUTES.REGISTER, { 
      state: { 
        editMode: true,
        registrationData: formattedData
      },
      replace: true
    });
  };

  const getPendingContent = () => ({
    icon: <PendingIcon sx={{ fontSize: 48, color: theme.palette.warning.main }} />,
    title: "Cadastro em Análise",
    message: "Estamos avaliando o cadastro da sua ONG com muito carinho! Em breve você receberá uma resposta. Enquanto isso, que tal conhecer mais sobre nossa plataforma?",
    style: {
      background: '#FFFFFF',
      borderRadius: { xs: 2, sm: 3 },
    }
  });

  const getRejectedContent = () => ({
    icon: <CancelIcon sx={{ fontSize: 48, color: theme.palette.error.main }} />,
    title: "Cadastro Não Aprovado",
    message: "Infelizmente, não foi possível aprovar o cadastro da sua ONG neste momento.",
    style: {
      background: '#FFFFFF',
      borderRadius: { xs: 2, sm: 3 },
    }
  });

  const content = status === 'rejected' ? getRejectedContent() : getPendingContent();

  const getTranslatedRejectionReason = (reason) => {
    return REJECTION_REASONS[reason] || reason;
  };

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Box sx={{ mb: 3 }}>
        {content.icon}
      </Box>
      
      <Typography 
        variant="h5" 
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '1.8rem' },
          fontWeight: 600,
          mb: 2,
          color: status === 'rejected' ? theme.palette.error.main : theme.palette.warning.main
        }}
      >
        {content.title}
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          fontSize: { xs: '1rem', sm: '1.1rem' },
          color: theme.palette.text.primary,
          mb: 3,
          maxWidth: '600px',
          mx: 'auto'
        }}
      >
        {content.message}
      </Typography>

      {status === 'rejected' && (
        <>
          <Box sx={{ 
            border: `1px solid ${theme.palette.divider}`,
            p: 4,
            borderRadius: 2,
            mb: 4,
            maxWidth: '600px',
            mx: 'auto',
            background: '#FFFFFF',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(to bottom, #294BB6, #4F97FF)',
              borderRadius: '4px 0 0 4px'
            }
          }}>
            <Box sx={{ pl: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box component="span" sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)'
                }} />
                Motivo da Rejeição
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: theme.palette.text.primary,
                  mb: observations ? 4 : 0,
                  lineHeight: 1.6
                }}
              >
                {getTranslatedRejectionReason(rejectionReason)}
              </Typography>

              {observations && (
                <>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 3,
                      mt: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box component="span" sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)'
                    }} />
                    Observações Adicionais
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.primary,
                      lineHeight: 1.6
                    }}
                  >
                    {observations}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              mb: 4,
              fontStyle: 'italic',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Você pode editar seu cadastro corrigindo os pontos mencionados acima.
            Nossa equipe está à disposição para ajudar no que for preciso!
          </Typography>

          <Button
            onClick={handleEditRegistration}
            variant="contained"
            sx={{ 
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              borderRadius: { xs: 1.5, sm: 2 },
              background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #4F97FF 0%, #294BB6 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(41, 75, 182, 0.25)'
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            Editar Cadastro
          </Button>
        </>
      )}

      {status === 'pending' && (
        <Button
          component={Link}
          to={APP_ROUTES.ROOT}
          variant="contained"
          sx={{ 
            py: { xs: 1.25, sm: 1.5 },
            px: { xs: 3, sm: 4 },
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 600,
            borderRadius: { xs: 1.5, sm: 2 },
            background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
            transition: 'all 0.3s ease',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(90deg, #4F97FF 0%, #294BB6 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(41, 75, 182, 0.25)'
            },
            '&:active': {
              transform: 'translateY(0)'
            }
          }}
        >
          Conhecer a Plataforma
        </Button>
      )}
    </Box>
  );
} 